// WHAT: Daily Session Summaries Hook
// WHY:  Fetches daily session summaries from backend with React Query caching
// DOMAIN: Productivity → Miracle Flow
// DEPENDENCIES: TanStack Query, Tauri core API
// RISKS:
//   - NOTE: Query key is hardcoded (may conflict if used in multiple places)
//   - NOTE: Stale time is 5 minutes (may show outdated data)
//   - NOTE: throwOnError is false (errors are silently ignored)

import { useQuery } from "@tanstack/react-query";
import { core } from "@tauri-apps/api";

export interface SessionInputSummary {
  input_type: string;
  question?: string;
  step: string;
  value: string;
}

export interface SessionSummary {
  completed_at: string;
  inputs: SessionInputSummary[];
  session_id: string;
  session_type: "Evening" | "Morning";
}

export interface DailySessionSummary {
  date: string;
  date_display: string;
  evening_session?: SessionSummary;
  has_both_sessions: boolean;
  morning_session?: SessionSummary;
}

/**
 * WHAT:
 *   React Query hook for fetching daily session summaries.
 *
 * WHY:
 *   Provides cached, reactive access to session summaries with automatic refetching.
 *
 * OUTPUT:
 *   - UseQueryResult<DailySessionSummary[]> — React Query result with data, loading, error states
 *
 * BEHAVIOR:
 *   - Fetches summaries via Tauri command 'get_daily_session_summaries'
 *   - Caches data for 5 minutes (staleTime)
 *   - Keeps data in cache for 15 minutes (gcTime)
 *   - Retries failed requests up to 2 times
 *   - Refetches when window regains focus
 *   - Errors are not thrown (throwOnError: false)
 *
 * RISKS:
 *   - NOTE: Data may be stale for up to 5 minutes
 *   - NOTE: Errors are silently ignored (no error handling in hook)
 *   - NOTE: Query key is hardcoded (may cause cache conflicts)
 *
 * @returns React Query result with daily session summaries
 *
 */
export const useDailySessionSummaries = () => {
  return useQuery<DailySessionSummary[]>({
    gcTime: 1000 * 60 * 15, // 15 minutes
    queryFn: () => core.invoke<DailySessionSummary[]>("get_daily_session_summaries"),
    queryKey: ["miracle-flow", "daily-session-summaries"],
    refetchOnWindowFocus: true,
    retry: 2,
    staleTime: 1000 * 60 * 5, // 5 minutes
    throwOnError: false,
  });
};
